import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { BarterWarning } from '../barter/BarterWarning';
import { useMe } from '../auth/useMe';
import { ViewersFormField } from '../events/ViewersField';
import { ClientField, DaysFields, EquipmentSourceField, PickedDaysField, QuoteSwitch, ReservationField, ScopePicker, useCustomScopes } from '../events/EventFields';
import { PlaceField } from '../events/PlaceField';
import { visiblePlace } from '../events/place';
import { autoRouteHint } from '../events/defaultRoute';
import { leaveWithoutAsking, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { addressWithoutName } from '../events/place';
import { usePresets } from '../settings/checklists';
import { PresetPicker } from '../settings/PresetChoice';
import { systemLabel, useDefaultSystem, useSystems } from '../settings/systems';
import { SystemChoice } from '../settings/SystemChoice';
import { scopeNames } from '../events/fields';
import { EVENTS_RESOURCE, RESERVATION_LABELS, formatEventRange, formatSegments, datesToInstants, runsOf, segmentsFromDays } from '../events/labels';
import { CREATE_STEP, emptyWizard, MAX_TERMS, missingForStep, noGear, stepsFor, wizardBody, type WizardDraft } from './wizard';
import type { CreatedInPanel } from '../events/afterCreate';
import { maySeeScopeLines, readScopeLines } from '../events/scopeLines';
import { NewPanelHead } from '../events/NewPanelHead';
import { useCalendarLink, type CalendarLink } from '../events/calendarLink';
import { datesOfPick, pickOfDates } from '../events/pickDates';
import type { TemplateSuggestion } from './types';
import './planning.css';
import { refreshEvents } from '../events/refresh';
import { autoPlaceHint, fillIfUnchanged, freshPlaceOf, isAutoFilled, placeOfDraft, useAutoPlace } from '../events/autoPlace';
import { offerPlaceRule } from '../events/RememberRule';
import { emptyRepeat, requestKey, RepeatSection, type RepeatDraft } from '../events/RepeatForm';

type Created = { eventId: string; templateId: string | null };

/** Where a card opened by a link lands: a line said once on that event (e.g. "Przejdź do kosztorysu" of the packlist screen). */
export type WizardLanding = { notice: string };

/** The start of the wizard's first day (for the traffic of "Pokaż trasę"), null while it is not a day. */
function wizardStart(d: WizardDraft): string | null {
  const first = d.days[0] ?? d.firstDay;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(first)) return null;
  try {
    return datesToInstants({ firstDay: first, firstTime: d.firstTime, lastDay: first, lastTime: '' }).startsAt;
  } catch {
    return null;
  }
}

/**
 * "Nowe wydarzenie" (planning.wizard, until 20.10 "Nowa impreza") in the side panel of "Wydarzenia" (owner 2026-10-21
 * 23:31: "robimy w zakładce tak jak wszystko do tej pory zamiast w nowym oknie"; "Pełny widok" like the card). "Dalej"
 * stays off until the step's required fields are there. "Utwórz i przejdź do sprzętówki" (step "Podsumowanie") makes
 * the event, its card and checklist on the server, puts the template's gear on the packlist and, with "Potrzebna
 * wycena", starts its quote (the rules put the services in). Then the panel shows the event's card on its tab
 * "Sprzętówka" — the packlist editor itself (`onCreated`); "Bez sprzętu" opens "Szczegóły".
 */
export function NewEventWizard({
  initialDay = null,
  initialDays = [],
  onCreated,
  onClose,
  full = false,
  onFullChange,
  phone = false,
  calendar,
}: {
  /** A double click on that day of the calendar (P6, `dzien=`); days picked with Shift / Cmd (`dni=`, owner 2026-10-21 00:44). */
  initialDay?: string | null | undefined;
  initialDays?: readonly string[] | undefined;
  onCreated: (created: CreatedInPanel) => void;
  onClose: () => void;
  full?: boolean;
  onFullChange?: ((full: boolean) => void) | undefined;
  phone?: boolean;
  /** The calendar beside the panel picks the dates (owner 2026-10-22 14:40); the other fields stay as typed. */
  calendar?: CalendarLink | undefined;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  // how the wizard opened: anything else is a change (owner 2026-10-23 13:34), dates picked in the calendar too
  const [fresh] = useState<WizardDraft>(() => emptyWizard((f) => !me || fieldVisible(me, EVENTS_RESOURCE, f), initialDay, initialDays));
  const [draft, setDraft] = useState<WizardDraft>(fresh);
  /** The system set by itself once the list loads (not a change of the person). */
  const autoSystem = useRef<string | null>(null);
  const set = (patch: Partial<WizardDraft>) => setDraft((d) => ({ ...d, ...patch }));
  useCalendarLink(calendar, pickOfDates(draft), (days) => set(datesOfPick(days)));
  // "Wydarzenie cykliczne" (owner 2026-10-21 06:58): the series is made when the new card is left, with the filled packlist
  const [repeatOn, setRepeatOn] = useState(false);
  const [repeat, setRepeat] = useState<RepeatDraft>(() => emptyRepeat(me, draft.firstDay));
  const [repeatKey] = useState(requestKey);
  useDefaultSystem(
    draft.systemId,
    (systemId) => {
      autoSystem.current = systemId;
      set({ systemId });
    },
    !!me && fieldVisible(me, EVENTS_RESOURCE, 'systemId'),
  );
  // owner 2026-10-30 22:20 / 22:23: the place of a rule in the name, else the default place (only a prefill)
  const autoPlace = useAutoPlace(
    draft.name,
    placeOfDraft(draft),
    (p, from) =>
      setDraft((d) => {
        const { placePoint: _point, ...filled } = fillIfUnchanged(d, from, p) as WizardDraft & { placePoint?: unknown };
        return filled;
      }),
    !!me && ['location', 'locationAddress', 'locationPoint', 'locationApprox'].every((f) => fieldVisible(me, EVENTS_RESOURCE, f)),
  );
  const heading = useRef<HTMLHeadingElement>(null);
  const scopes = useCustomScopes(!!me && can(me, 'events.scopes', 'create'));
  const seeTemplates = !!me && can(me, 'templates.templates', 'view');
  const systems = useSystems();
  const presets = usePresets(!!me && can(me, 'planning.cards', 'view'));
  const first = useRef(true);

  // every later step starts at its heading, so a screen reader and the keyboard begin at the top (the first: the panel's title)
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    heading.current?.focus();
  }, [step]);

  const suggestions = useQuery({
    queryKey: ['planning', 'templates', draft.scopes.join(',')],
    enabled: seeTemplates && step >= 2,
    queryFn: () => api<{ templates: TemplateSuggestion[] }>(`/api/planning/templates?scopes=${encodeURIComponent(draft.scopes.join(','))}`),
  });
  const chosen = suggestions.data?.templates.find((t) => t.id === draft.templateId) ?? null;

  const create = useMutation({
    mutationFn: async (): Promise<{ created: Created; notice: string }> => {
      const created = await api<Created>('/api/planning/events', { method: 'POST', body: { ...wizardBody(draft, (f) => !me || fieldVisible(me, EVENTS_RESOURCE, f)), ...autoRouteHint(queryClient, draft) } });
      const notes = ['Wydarzenie utworzone.'];
      // P15: "Bez sprzętu" takes only the template's checklist (made by the server), never its gear
      if (created.templateId && noGear(draft)) notes.push('Lista kontrolna z szablonu jest w wydarzeniu; sprzętu z szablonu nie wstawiono („Bez sprzętu”).');
      if (created.templateId && !noGear(draft)) {
        const name = chosen?.name ? `„${chosen.name}”` : 'z szablonu';
        try {
          await api(`/api/packlists/${created.eventId}/apply-template`, { method: 'POST', body: { templateId: created.templateId } });
          notes.push(`Sprzęt i lista kontrolna z szablonu ${name} są już w wydarzeniu.`);
        } catch (error) {
          const why = error instanceof ApiError ? error.message : 'błąd połączenia';
          notes.push(`Lista kontrolna jest, ale sprzętu z szablonu ${name} nie wstawiono (${why}). Wstaw go w zakładce „Sprzętówka”.`);
        }
      }
      // P15: "Potrzebna wycena" starts the quote at once; the rules put the services of the scopes in ("Obsługa")
      if (draft.needsQuote && me && can(me, 'quotes.quotes', 'create')) {
        try {
          await api(`/api/quotes/events/${created.eventId}`, { method: 'POST' });
          // owner 2026-10-30: the scope items' lines by name („Realizator FOH”, „Technik”…)
          const scoped = maySeeScopeLines(me) ? ((await readScopeLines(created.eventId, false))?.lines ?? []) : [];
          // M1: names only when the lines' „Nazwa” is seen
          const shown = scoped.length > 0 && scoped.every((l) => l.name !== null);
          notes.push(shown ? `Kosztorys założony — w „Obsługa”: ${scoped.map((l) => `„${l.name}”`).join(', ')}.` : 'Kosztorys założony — usługi z zakresu prac są już w „Obsługa”.');
        } catch (error) {
          notes.push(`Kosztorysu nie założono (${error instanceof ApiError ? error.message : 'błąd połączenia'}). Załóż go w zakładce „Kosztorys”.`);
        }
      }
      return { created, notice: notes.join(' ') };
    },
    // a problem with the picked days is shown at the days (review I3): back to the first step
    onError: (e) => {
      if (e instanceof ApiError && fieldIssues(e).some((i) => i.path === 'days')) setStep(0);
    },
    onSuccess: async ({ created: made, notice }) => {
      await refreshEvents(queryClient);
      // a place of its own: „Zapamiętać: „PFK” → …?” (owner 2026-10-30 22:23)
      offerPlaceRule(me, draft.name, placeOfDraft(draft), autoPlace.state, autoPlace.rules);
      const gear = !noGear(draft);
      onCreated({
        eventId: made.eventId,
        notice: gear ? `${notice} Dodaj sprzęt w tabeli albo zamknij kartę — dodasz go później.` : notice,
        tab: gear ? 'packlist' : 'details',
        series: repeatOn ? { draft: repeat, firstDay: draft.firstDay, key: repeatKey } : null,
        remind: gear,
      });
    },
  });

  // owner 2026-10-23 13:34: closing with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”; „Zapisz” creates the event
  const guardRef = useUnsavedGuard({
    dirty: repeatOn || !sameValues({ ...draft, systemId: draft.systemId === autoSystem.current ? fresh.systemId : draft.systemId, ...(isAutoFilled(autoPlace.state, placeOfDraft(draft)) ? freshPlaceOf(fresh) : {}) }, fresh),
    save: async () => {
      const short = [0, 1, 2].find((s) => missingForStep(s, draft).length > 0);
      if (short !== undefined) {
        // the step says what is missing ("Uzupełnij: …")
        setStep(short);
        return false;
      }
      await create.mutateAsync();
      return true;
    },
  });

  if (!me) return null;
  const STEPS = stepsFor(draft);
  const missing = missingForStep(step, draft);
  const last = step === CREATE_STEP;
  const error = create.error instanceof ApiError ? create.error : null;

  function next(e: FormEvent) {
    e.preventDefault();
    if (missing.length > 0 || create.isPending) return;
    if (last) create.mutate();
    else setStep(step + 1);
  }

  return (
    <section ref={guardRef} className="panel event-card wizard new-in-panel" aria-label="Nowe wydarzenie" data-feature-id="planning.wizard">
      <NewPanelHead title="Nowe wydarzenie" full={full} onFullChange={onFullChange} phone={phone} onClose={onClose} />
      <ol className="wizard-steps" aria-label="Kroki kreatora">
        {STEPS.map((label, i) => (
          <li key={label} aria-current={i === step ? 'step' : undefined} className={i < step ? 'past' : undefined}>
            {i < step ? (
              <button type="button" onClick={() => setStep(i)}>
                <span className="wizard-step-no" aria-hidden="true">{i + 1}</span>
                {label}
              </button>
            ) : (
              <span>
                <span className="wizard-step-no" aria-hidden="true">{i + 1}</span>
                {label}
              </span>
            )}
          </li>
        ))}
      </ol>

      <form className="wizard-form" aria-label={`Krok ${step + 1} z ${STEPS.length}: ${STEPS[step]}`} onSubmit={next} noValidate>
        <h3 ref={heading} tabIndex={-1}>
          {STEPS[step]}
        </h3>

        {step === 0 && (
          <>
            <div className="group">
              <label className="field">
                <span>Nazwa wydarzenia</span>
                <input value={draft.name} onChange={(e) => set({ name: e.target.value })} autoComplete="off" placeholder="np. Coldplay — PGE Narodowy" />
              </label>
              <ClientField picked={draft.client} onPick={(client) => set({ client })} canCreate={can(me, 'contractors.contractors', 'create')} />
              {draft.client && <BarterWarning contractorId={draft.client.id} contractorName={draft.client.name} />}
              {fieldVisible(me, EVENTS_RESOURCE, 'viewerIds') && <ViewersFormField value={draft.viewerIds ?? [me.id]} onChange={(viewerIds) => set({ viewerIds })} />}
            </div>
            <div className="group">
              <PlaceField
                value={visiblePlace(fieldVisible(me, EVENTS_RESOURCE, 'routeKm') ? draft : { ...draft, routeKm: undefined }, (f) => fieldVisible(me, EVENTS_RESOURCE, f))}
                onChange={(patch) => set(patch)}
                name={draft.name}
                startsAt={wizardStart(draft)}
                hint={autoPlaceHint(autoPlace.state, placeOfDraft(draft))}
                filled={isAutoFilled(autoPlace.state, placeOfDraft(draft))}
              />
            </div>
            <div className="group">
              {draft.days.length > 1 ? (
                <PickedDaysField
                  days={draft.days}
                  firstTime={draft.firstTime}
                  lastTime={draft.lastTime}
                  onTimes={(patch) => set(patch)}
                  onSingle={() => set({ days: [] })}
                  error={(error ? fieldIssues(error).find((i) => i.path === 'days')?.message : undefined) ?? (runsOf(draft.days).length > MAX_TERMS ? `Najwyżej ${MAX_TERMS} terminów — zostaw mniej osobnych dni.` : undefined)}
                />
              ) : (
                <DaysFields value={draft} onChange={(patch) => set(patch)} />
              )}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="group">
              <ScopePicker value={draft.scopes} onChange={(s) => set({ scopes: s, templateId: null })} custom={scopes.custom} onAdd={scopes.add} />
            </div>
            <div className="group">
              <EquipmentSourceField value={draft.equipmentSource} onChange={(equipmentSource) => set({ equipmentSource })} />
              {/* the system (once in the gone step "Sprzętówka"); without gear the event gets "Bez systemu" */}
              {!noGear(draft) && fieldVisible(me, EVENTS_RESOURCE, 'systemId') && <SystemChoice value={draft.systemId} onChange={(systemId) => set({ systemId })} />}
            </div>
            <div className="group">
              <ReservationField value={draft.reservationKind} onChange={(reservationKind) => set({ reservationKind })} />
              <QuoteSwitch value={draft.needsQuote} onChange={(needsQuote) => set({ needsQuote })} />
            </div>
          </>
        )}

        {step === 2 && (
          <div className="group">
            {!seeTemplates && <p className="muted">Nie masz wglądu w szablony wydarzeń. Przejdź dalej — wydarzenie powstanie bez szablonu.</p>}
            {seeTemplates && suggestions.isLoading && <p className="muted" role="status">Szukam pasujących szablonów…</p>}
            {seeTemplates && suggestions.data && (
              <fieldset className="template-choice">
                <legend>Szablony pasujące do zakresu: {scopeNames(draft.scopes, scopes.custom).join(', ')}</legend>
                {suggestions.data.templates.length === 0 && <p className="muted">Żaden szablon nie pasuje do tego zakresu. Wydarzenie powstanie bez szablonu.</p>}
                {suggestions.data.templates.map((t) => (
                  <label key={t.id} className={`template-option${draft.templateId === t.id ? ' on' : ''}`}>
                    <input type="radio" name="templateId" checked={draft.templateId === t.id} onChange={() => set({ templateId: t.id })} />
                    <span className="template-option-text">
                      <strong>{t.name ?? 'Szablon'}</strong>
                      <span className="hint">
                        {t.scopes ? `${scopeNames(t.scopes, []).join(', ')}. ` : ''}Pasuje {t.matched} z {draft.scopes.length} wybranych zakresów.
                      </span>
                    </span>
                  </label>
                ))}
                <label className={`template-option${draft.templateId === null ? ' on' : ''}`}>
                  <input type="radio" name="templateId" checked={draft.templateId === null} onChange={() => set({ templateId: null })} />
                  <span className="template-option-text">
                    <strong>Bez szablonu</strong>
                    <span className="hint">Sprzęt dodasz później w sprzętówce.</span>
                  </span>
                </label>
              </fieldset>
            )}
            {suggestions.error && <p className="alert" role="alert">{suggestions.error instanceof ApiError ? suggestions.error.message : 'Nie udało się wczytać szablonów'}</p>}
          </div>
        )}
        {step === 2 && (
          <div className="group">
            <PresetPicker value={draft.checklistPresetIds} onChange={(checklistPresetIds) => set({ checklistPresetIds })} />
          </div>
        )}

        {step === 3 && (
          <div className="group">
            <dl className="details-list summary">
              <Row label="Nazwa" value={draft.name.trim()} />
              <Row label="Klient" value={draft.client?.name ?? ''} />
              <Row label="Miejsce" value={[draft.location.trim(), addressWithoutName(draft.locationAddress.trim(), draft.location)].filter(Boolean).join(', ') + (draft.locationApprox && (draft.locationAddress.trim() || draft.locationPoint) ? ' (lokalizacja nieprecyzyjna)' : '')} />
              {draft.days.length > 1 ? (
                <Row label="Terminy" value={formatSegments(segmentsFromDays(draft.days, draft.firstTime, draft.lastTime))} />
              ) : (
                <Row label="Termin" value={formatEventRange(datesToInstants(draft).startsAt, datesToInstants(draft).endsAt)} />
              )}
              <Row label="Zakres prac" value={scopeNames(draft.scopes, scopes.custom).join(', ')} />
              {noGear(draft) ? <Row label="System" value="Bez systemu" /> : <Row label="System" value={systemLabel(systems.data?.systems ?? [], draft.systemId)} />}
              <Row label="Skąd sprzęt" value={draft.equipmentSource.trim()} />
              <Row label="Rezerwacja sprzętu" value={RESERVATION_LABELS[draft.reservationKind]} />
              <Row label="Potrzebna wycena" value={draft.needsQuote ? 'Tak' : 'Nie'} />
              <Row label="Szablon" value={draft.templateId ? (chosen?.name ?? 'wybrany') : 'Bez szablonu'} />
              {draft.checklistPresetIds.length > 0 && <Row label="Listy kontrolne" value={(presets.data?.presets ?? []).filter((p) => draft.checklistPresetIds.includes(p.id)).map((p) => p.name).join(', ')} />}
            </dl>
            {afterCreate(draft) && <p className="hint">{afterCreate(draft)}</p>}
          </div>
        )}
        {step === 3 && <RepeatSection on={repeatOn} onToggle={setRepeatOn} draft={repeat} onChange={setRepeat} firstDay={draft.firstDay} />}

        {error && (
          <p className="alert" role="alert">
            {fieldIssues(error).map((i) => i.message).join(' ') || error.message}
          </p>
        )}

        <div className="wizard-actions">
          <button className="btn ghost" type="button" onClick={() => leaveWithoutAsking(onClose)}>
            Anuluj
          </button>
          <span className="wizard-actions-end">
            {step > 0 && (
              <button className="btn" type="button" onClick={() => setStep(step - 1)}>
                Wstecz
              </button>
            )}
            <button className="btn primary" type="submit" disabled={missing.length > 0 || create.isPending} aria-describedby={missing.length > 0 ? 'wizard-missing' : undefined}>
              {last ? (create.isPending ? 'Tworzenie…' : noGear(draft) ? 'Utwórz wydarzenie' : 'Utwórz i przejdź do sprzętówki') : 'Dalej'}
            </button>
          </span>
        </div>
        {missing.length > 0 && (
          <p id="wizard-missing" className="hint wizard-missing">
            Uzupełnij: {missing.join(', ')}.
          </p>
        )}
      </form>
    </section>
  );
}

/** What creating will add besides the event (template gear, checklist points). */
function afterCreate(d: WizardDraft): string {
  const lists = d.checklistPresetIds.length > 0;
  const parts = [
    d.templateId && noGear(d)
      ? `Po utworzeniu punkty szablonu${lists ? ' i wybranych list' : ''} trafią na listę kontrolną; sprzętu z szablonu nie wstawimy („Bez sprzętu”).`
      : d.templateId
        ? `Po utworzeniu sprzęt z szablonu trafi do sprzętówki, a punkty szablonu${lists ? ' i wybranych list' : ''} — na listę kontrolną.`
        : lists ? 'Po utworzeniu punkty wybranych list trafią na listę kontrolną.' : '',
    d.needsQuote ? 'Powstanie też kosztorys; reguły dodadzą usługi z zakresu prac (np. Realizacja FOH → Realizator FOH).' : '',
    noGear(d) ? '' : 'Potem karta wydarzenia na zakładce „Sprzętówka”: dodasz sprzęt w tabeli albo zamkniesz kartę i dodasz go później.',
  ];
  return parts.filter(Boolean).join(' ');
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
